import { useState } from "react";
import classNames from "classnames";
import { FormattedMessage, useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import Select from 'react-select';

import { updateSearch, resetSearch } from "../../../../redux/slices/cvSlice";

const stages = ["SelectCV", "AddJobSpec", "Requirements", "FindMatches"]

const JobMatchSidebar = ({ showFilters = false }) => {
  const dispatch = useDispatch();

  const selectedCVs = useSelector((state) => state.jobs.selectedCVs);
  const search = useSelector((state) => state.cv.search);
  const cvs = useSelector((state) => state.cv.cvs);
  const labels = useSelector((state) => state.cv.labels);

  const { formatMessage } = useIntl();
  const { pathname } = useLocation();
  const activeStage = pathname.includes("/select-cv")
    ? "SelectCV"
    : pathname.includes("/job-spec")
      ? "AddJobSpec"
      : pathname.includes("/requirements")
        ? "Requirements"
        : pathname.includes("/matches")
          ? "FindMatches"
          : ""

  const today = new Date().toISOString().split("T")[0];

  const setSearchByKeyword = (keyword = "") => {
    dispatch(updateSearch({ keyword }));
  }

  const setUploadDate = ({ from, to }) => {
    dispatch(updateSearch({ uploadDate: { from, to } }));
  };

  const setSelectedLabels = (selectedLabelsOptions) => {
    dispatch(updateSearch({
      ...search,
      selectedLabels: selectedLabelsOptions,
    }));
  };

  const setActivitySector = (activitySectorOptions) => {
    dispatch(updateSearch({
      ...search,
      occupation: {
        ...search.occupation,
        activitySector: activitySectorOptions,
      }
    }));
  };

  const setOccupation = (occupationOptions) => {
    dispatch(updateSearch({
      ...search,
      occupation: {
        ...search.occupation,
        occupation: occupationOptions,
      }
    }));
  };

  const setCountry = (countryOptions) => {
    dispatch(updateSearch({
      ...search,
      location: {
        ...search.location,
        country: countryOptions,
      }
    }));
  };

  const setCity = (cityOptions) => {
    dispatch(updateSearch({
      ...search,
      location: {
        ...search.location,
        city: cityOptions,
      }
    }));
  };

  const setHardSkills = (hardSkillsOptions) => {
    dispatch(updateSearch({
      ...search,
      skills: {
        ...search.skills,
        hardSkills: hardSkillsOptions,
      }
    }));
  };

  const stepItems = buildStepItems({
    activeStage,
    selectedCVsLength: selectedCVs?.length,
    formatMessage
  })

  const {
    activitySectorOptions = [],
    occupationOptions = [],
    countryOptions = [],
    cityOptions = [],
    hardSkillsOptions = []
  } = calculateOptions(cvs);

  const labelOptions = labels?.map((label) => ({
    value: label.id,
    label: label.name,
    color: label.color,
  })) || [];

  const hasFilters =
    search.keyword ||
    search.uploadDate?.from || search.uploadDate?.to ||
    search.occupation.activitySector.length > 0 ||
    search.occupation.occupation.length > 0 ||
    search.location.country.length > 0 ||
    search.location.city.length > 0 ||
    search.skills.hardSkills.length > 0;

  return (
    <div className="job-match">
      {stepItems.map((item, i) =>
        <Steps item={item} i={i} key={`Step - ${i}`} />
      )}

      {showFilters && (

        <div className="job-match__filters">
          <FiltersExpandedSection isSearch label={formatMessage({ id: "search.label.advancedSearch" })} >
            <div className={classNames("search", { "search--disabled": false })}>
              <input
                value={search.keyword}
                onChange={({ target }) => !false && setSearchByKeyword(target.value)}
                placeholder={formatMessage({ id: "search.findByName" })}
                disabled={false}
                className={classNames({ "disabled": false })}
              />
            </div>

            <Select
              isMulti
              isSearchable
              isClearable
              placeholder={formatMessage({ id: "search.filterByLabels" })}
              className="filter-select"
              options={labelOptions}
              styles={{
                option: (baseStyles, { data }) => ({
                  ...baseStyles,
                  color: 'white',
                  backgroundColor: data.color, // Adding transparency
                  fontWeight: 900,
                  margin: "8px 3%",
                  width: '94%',
                  cursor: 'pointer',
                  paddingTop: '6px',
                  paddingBottom: '6px',
                  fontSize: '14px',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                }),
                multiValue: (baseStyles, { data }) => ({
                  ...baseStyles,
                  backgroundColor: data.color,
                  color: 'white',
                  fontWeight: '700',
                }),
                multiValueLabel: (baseStyles) => ({
                  ...baseStyles,
                  color: 'white',
                }),
                multiValueRemove: (styles) => ({
                  ...styles,
                  ':hover': {
                    backgroundColor: "#CCCCCC66",
                    cursor: 'pointer'
                  },
                }),
              }}
              value={search.selectedLabels}
              onChange={setSelectedLabels}
            />
          </FiltersExpandedSection>

          <FiltersExpandedSection label={formatMessage({ id: "search.label.uploadDate" })} >
            <label className="date-label">
              <FormattedMessage id="search.from" />
              <input
                className="date-input"
                type="date"
                value={search.uploadDate.from}
                onChange={({target}) => setUploadDate({ from: target.value, to: search.uploadDate.to })}
                max={today}
              />
            </label>
            <label className="date-label">
              <FormattedMessage id="search.to" />
              <input
                className="date-input"
                type="date"
                value={search.uploadDate.to || today}
                onChange={({target}) => setUploadDate({ from: search.uploadDate.from, to: target.value })}
                min={search.uploadDate.from || ""}
                max={today}
              />
            </label>
          </FiltersExpandedSection>


          <FiltersExpandedSection label={formatMessage({ id: "search.label.occupation" })} >
            <Select
              isMulti
              isSearchable
              isClearable
              placeholder={"Filter by Activity Sector"}
              className="filter-select"
              options={activitySectorOptions}
              value={search.occupation.activitySector}
              onChange={setActivitySector}
            />
            <Select
              isMulti
              isSearchable
              isClearable
              placeholder={"Filter by Occupation"}
              className="filter-select"
              options={occupationOptions}
              value={search.occupation.occupation}
              onChange={setOccupation}
            />
          </FiltersExpandedSection>

          <FiltersExpandedSection label={formatMessage({ id: "search.label.location" })} >
            <Select
              isMulti
              isSearchable
              isClearable
              placeholder={"Filter by City"}
              className="filter-select"
              options={cityOptions}
              value={search.location.city}
              onChange={setCity}
            />
            <Select
              isMulti
              isSearchable
              isClearable
              placeholder={"Filter by Country"}
              className="filter-select"
              options={countryOptions}
              value={search.location.country}
              onChange={setCountry}
            />
          </FiltersExpandedSection>

          <FiltersExpandedSection label={formatMessage({ id: "search.label.skills" })} >
            <Select
              isMulti
              isSearchable
              isClearable
              placeholder={formatMessage({ id: "search.selectBySkills" })}
              className="filter-select"
              options={hardSkillsOptions}
              value={search.skills.hardSkills}
              onChange={setHardSkills}
            />
          </FiltersExpandedSection>

          <button
            className={classNames("btn quinary btn--square clear-filers-btn", {
              "clear-filers-btn__disabled": !hasFilters,
            })}
            onClick={() => dispatch(resetSearch())}
            disabled={!hasFilters}
          >
            <FormattedMessage id="search.clearFilters" />
          </button>
        </div>
      )}

    </div>
  )
}

const buildStepItems = ({
  activeStage = "",
  selectedCVsLength = 0,
  formatMessage
}) => {
  const highlightAddSpecLink = selectedCVsLength > 0 && activeStage === "SelectCV";
  const activeStageIndex = stages.indexOf(activeStage);

  return ([
    {
      name:
        selectedCVsLength > 0
          ? formatMessage(
              { id: "common.cvsSelected" },
              { count: selectedCVsLength },
            )
          : formatMessage({ id: "sidebar.selectCVs" }),
      active: activeStage === "SelectCV",
      done: activeStageIndex > 0,
      link: "/job-match/select-cv?from=sidebar",
    },
    {
      name: formatMessage({ id: "sidebar.addJobSpec" }),
      active: activeStage === "AddJobSpec",
      done: activeStageIndex > 1,
      highlight: highlightAddSpecLink,
      link: highlightAddSpecLink ? "/job-match/job-spec" : undefined,
    },
    {
      name: formatMessage({ id: "sidebar.requirements" }),
      active: activeStage === "Requirements",
      done: activeStageIndex > 2,
    },
    {
      name: formatMessage({ id: "sidebar.findMatches" }),
      active: activeStage === "FindMatches",
      done: activeStageIndex > 3,
    }
  ]);
};

const Steps = ({item, i}) => item.link ? (
  <Link
    to={item.link}
    key={`Item - ${i}`}
    className={classNames("job-match__item", {
      "job-match__item--highlight": item.highlight,
      "job-match__item--active": item.active,
      "job-match__item--completed": item.done,
    })}
  >
    {item.highlight
      ? highlightSvg
      : item.done ? doneSvg : <span>{i + 1}</span>
    }
    {item.name}
  </Link>
) : (
  <p
    to={item.link}
    key={`Item - ${i}`}
    className={classNames("job-match__item", {
      "job-match__item--active": item.active,
      "job-match__item--completed": item.done,
    })}
  >
    {item.done ? doneSvg : <span>{i + 1}</span>}
    {item.name}
  </p>
);

const FiltersExpandedSection = ({ isSearch, label = "", children }) => {
  const [expandedSection, setExpandedSection] = useState(isSearch);

  return (
    <div className="filters-container">
      <label
        className={classNames("header-label", {
          "header-label__expanded": expandedSection && !isSearch,
          "header-label__search": isSearch,
        })}
        onClick={() => !isSearch && setExpandedSection((prev) => !prev)}
      >
        {label}
        {isSearch ? searchSvg : arrowSvg}
      </label>

      <div className={classNames("filters-container__filters-section", {
        "filters-container__filters-section--expanded": expandedSection,
      })}>
        {children}
      </div>
    </div>
  );
};

const calculateOptions = (cvs = []) => {
  const optionsMap = {
    activitySectorOptions: new Map(),
    occupationOptions: new Map(),
    countryOptions: new Map(),
    cityOptions: new Map(),
    hardSkillsOptions: new Map(),
  };

  cvs?.forEach(item => {
    // Process experience for activity sectors and occupations
    item.experience?.forEach(exp => {
      if (exp.activity_sector) {
        optionsMap.activitySectorOptions.set(
          exp.activity_sector,
          { value: exp.activity_sector, label: exp.activity_sector }
        );
      }
      if (exp.title) optionsMap.occupationOptions.set(exp.title, { value: exp.title, label: exp.title });
    });

    // Process address for countries and cities
    const { country, city } = item.information?.address || {};

    if (country) optionsMap.countryOptions.set(country, { value: country, label: country });
    if (city) optionsMap.cityOptions.set(city, { value: city, label: city });

    // Process hard skills
    item.hard_skills?.forEach(({ skill }) => {
      if (skill) optionsMap.hardSkillsOptions.set(skill, { value: skill, label: skill });
    });
  });

  // Convert Maps to sorted arrays
  const sortByLabel = (a, b) => a.label.localeCompare(b.label);
  return {
    activitySectorOptions: Array.from(optionsMap.activitySectorOptions.values()).sort(sortByLabel),
    occupationOptions: Array.from(optionsMap.occupationOptions.values()).sort(sortByLabel),
    countryOptions: Array.from(optionsMap.countryOptions.values()).sort(sortByLabel),
    cityOptions: Array.from(optionsMap.cityOptions.values()).sort(sortByLabel),
    hardSkillsOptions: Array.from(optionsMap.hardSkillsOptions.values()).sort(sortByLabel),
  };
};

const highlightSvg = (
  <svg width="25" height="25" viewBox="0 0 25 25" fill="none">
    <rect width="25" height="25" rx="12.5" fill="white" />
    <path
      d="M9 12.5H16M16 12.5L13 8.5M16 12.5L13 16.5"
      stroke="#5EC58F"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const doneSvg = (
  <span>
    <svg width="11" height="10" viewBox="0 0 11 10" fill="none">
      <path
        d="M5.58458 9.78764L10.1216 1.92932L8.41032 0.941321L4.75082 7.27976L1.7561 5.55076L0.8786 7.07064L5.58458 9.78764Z"
        fill="#002060"
      />
    </svg>
  </span>
);

const searchSvg = (
  <svg
    width="14"
    height="14"
    viewBox="0 0 21 21"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M19.3397 19.0625L14.9897 14.7125M17.3397 9.0625C17.3397 13.4808 13.758 17.0625 9.33972 17.0625C4.92144 17.0625 1.33972 13.4808 1.33972 9.0625C1.33972 4.64422 4.92144 1.0625 9.33972 1.0625C13.758 1.0625 17.3397 4.64422 17.3397 9.0625Z"
      stroke="#d1cfcf"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const arrowSvg = (
  <svg
    width="12"
    height="12"
    viewBox="0 0 9 8"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4.66985 7.8125L8.99998 0.3125H0.339722L4.66985 7.8125Z"
      fill="#d1cfcf"
    />
  </svg>
)

export default JobMatchSidebar
